(() => {
  function initializeTabs(root) {
    if (root.dataset.tabsInitialized === 'true') return;

    root.dataset.tabsInitialized = 'true';

    const buttons = [
      ...root.querySelectorAll(
        ':scope > .cholesterol-tabs__nav [data-tab-target]'
      )
    ];

    const panels = [
      ...root.querySelectorAll(
        ':scope > .cholesterol-tabs__panels > [data-tab-panel]'
      )
    ];

    function activate(name, moveFocus = false) {
      buttons.forEach((button) => {
        const active =
          button.dataset.tabTarget === name;

        button.classList.toggle(
          'is-active',
          active
        );

        button.setAttribute(
          'aria-selected',
          active ? 'true' : 'false'
        );

        button.tabIndex =
          active ? 0 : -1;

        if (active && moveFocus) {
          button.focus();
        }
      });

      panels.forEach((panel) => {
        const active =
          panel.dataset.tabPanel === name;

        panel.classList.toggle(
          'is-active',
          active
        );

        panel.setAttribute(
          'aria-hidden',
          active ? 'false' : 'true'
        );
      });
    }

    buttons.forEach((button, index) => {
      button.addEventListener(
        'click',
        () => {
          activate(
            button.dataset.tabTarget
          );
        }
      );

      button.addEventListener(
        'keydown',
        (event) => {
          if (
            ![
              'ArrowRight',
              'ArrowLeft',
              'Home',
              'End'
            ].includes(event.key)
          ) {
            return;
          }

          event.preventDefault();

          let next = index;

          if (
            event.key === 'ArrowRight'
          ) {
            next =
              (index + 1) %
              buttons.length;
          }

          if (
            event.key === 'ArrowLeft'
          ) {
            next =
              (index - 1 + buttons.length) %
              buttons.length;
          }

          if (event.key === 'Home') {
            next = 0;
          }

          if (event.key === 'End') {
            next =
              buttons.length - 1;
          }

          activate(
            buttons[next].dataset.tabTarget,
            true
          );
        }
      );
    });
  }

  function initializeSubtabs(container) {
    if (
      container.dataset.subtabsInitialized ===
      'true'
    ) {
      return;
    }

    container.dataset.subtabsInitialized =
      'true';

    const buttons = [
      ...container.querySelectorAll(
        ':scope > .cholesterol-subtabs__nav [data-subtab-target]'
      )
    ];

    const panels = [
      ...container.querySelectorAll(
        ':scope > .cholesterol-subtabs__views > [data-subtab-panel]'
      )
    ];

    function activate(name) {
      buttons.forEach((button) => {
        const active =
          button.dataset.subtabTarget ===
          name;

        button.classList.toggle(
          'is-active',
          active
        );

        button.setAttribute(
          'aria-selected',
          active ? 'true' : 'false'
        );
      });

      panels.forEach((panel) => {
        const active =
          panel.dataset.subtabPanel ===
          name;

        panel.classList.toggle(
          'is-active',
          active
        );

        panel.setAttribute(
          'aria-hidden',
          active ? 'false' : 'true'
        );
      });
    }

    buttons.forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          activate(
            button.dataset.subtabTarget
          );
        }
      );
    });
  }

  function initializeAccordions(root) {
    const accordions =
      root.querySelectorAll(
        'details.cholesterol-accordion'
      );

    accordions.forEach((details) => {
      if (
        details.dataset
          .accordionInitialized ===
        'true'
      ) {
        return;
      }

      details.dataset
        .accordionInitialized =
        'true';

      const summary =
        details.querySelector(
          ':scope > summary'
        );

      if (!summary) {
        return;
      }

      /*
        Only use data-default-open to
        define the INITIAL state.

        After initialization, the browser
        handles <details>/<summary> opening
        and closing natively.
      */
      if (
        details.hasAttribute(
          'data-default-open'
        )
      ) {
        details.open = true;
      }

      function synchronizeState() {
        summary.setAttribute(
          'aria-expanded',
          details.open
            ? 'true'
            : 'false'
        );
      }

      synchronizeState();

      /*
        IMPORTANT:
        We do NOT attach a click handler
        to <summary>.

        Native HTML controls the toggle.
      */
      details.addEventListener(
        'toggle',
        synchronizeState
      );
    });
  }

  function reveal(element) {
    if (!element) return;

    element.hidden = false;

    element.setAttribute(
      'aria-hidden',
      'false'
    );

    requestAnimationFrame(() => {
      element.classList.add(
        'is-visible'
      );
    });
  }

  function conceal(element) {
    if (!element) return;

    element.classList.remove(
      'is-visible'
    );

    element.hidden = true;

    element.setAttribute(
      'aria-hidden',
      'true'
    );
  }

  function initializeHormoneFlow(root) {
    const flow =
      root.querySelector(
        '[data-hormone-flow]'
      );

    if (
      !flow ||
      flow.dataset.flowInitialized ===
        'true'
    ) {
      return;
    }

    flow.dataset.flowInitialized =
      'true';

    const answers = [
      ...flow.querySelectorAll(
        '[data-hormone-answer]'
      )
    ];

    const resetButton =
      flow.querySelector(
        '[data-hormone-reset]'
      );

    const paths = {
      '1:no': [
        '[data-hormone-result="q1-no"]'
      ],

      '1:yes': [
        '[data-hormone-connector="1"]',
        '[data-hormone-step="2"]'
      ],

      '2:yes': [
        '[data-hormone-result="q2-yes"]'
      ],

      '2:no': [
        '[data-hormone-connector="2"]',
        '[data-hormone-step="3"]'
      ],

      '3:no': [
        '[data-hormone-result="q3-no"]'
      ],

      '3:yes': [
        '[data-hormone-connector="3"]',
        '[data-hormone-step="4"]'
      ],

      '4:yes': [
        '[data-hormone-result="q4-yes"]'
      ],

      '4:no': [
        '[data-hormone-connector="4"]',
        '[data-hormone-final]'
      ]
    };

    function resetFlow() {
      flow
        .querySelectorAll(
          '[data-hormone-step]'
        )
        .forEach((step) => {
          if (
            step.dataset
              .hormoneStep === '1'
          ) {
            step.hidden = false;

            step.setAttribute(
              'aria-hidden',
              'false'
            );

            step.classList.add(
              'is-visible'
            );
          } else {
            conceal(step);
          }
        });

      flow
        .querySelectorAll(
          '[data-hormone-result]'
        )
        .forEach(conceal);

      flow
        .querySelectorAll(
          '[data-hormone-connector]'
        )
        .forEach(conceal);

      conceal(
        flow.querySelector(
          '[data-hormone-final]'
        )
      );

      answers.forEach((button) => {
        button.disabled = false;

        button.classList.remove(
          'is-selected'
        );
      });
    }

    function lockStep(
      stepNumber,
      selectedButton
    ) {
      flow
        .querySelectorAll(
          `[data-hormone-answer][data-step="${stepNumber}"]`
        )
        .forEach((button) => {
          button.disabled = true;

          button.classList.toggle(
            'is-selected',
            button ===
              selectedButton
          );
        });
    }

    answers.forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const key =
            `${button.dataset.step}:${button.dataset.answer}`;

          lockStep(
            button.dataset.step,
            button
          );

          (paths[key] || []).forEach(
            (selector) => {
              reveal(
                flow.querySelector(
                  selector
                )
              );
            }
          );
        }
      );
    });

    if (resetButton) {
      resetButton.addEventListener(
        'click',
        resetFlow
      );
    }

    resetFlow();
  }

  function initializeAll(
    scope = document
  ) {
    scope
      .querySelectorAll(
        '[data-cholesterol-tabs]'
      )
      .forEach((root) => {
        initializeTabs(root);

        root
          .querySelectorAll(
            '[data-subtabs]'
          )
          .forEach(
            initializeSubtabs
          );

        initializeAccordions(root);

        initializeHormoneFlow(root);
      });
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeAll();
      }
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeAll(
        event.target
      );
    }
  );
})();